import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Pencil, Route, Waypoints, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '@/lib/api';
import { safeHex, textOn } from '@/lib/colors';
import { t } from '@/lib/i18n';
import type { LineCheck, ZoneRef } from '@/lib/stats';
import { LineCheckNotice } from './dashboard-widgets';
import { drawBus, drawStation, iconUrl } from './map/icons';
import { Button } from './ui/button';
import { Badge, Spinner } from './ui/layout';

interface StationInfo {
  station: { _id: string; name: string };
  lines: Array<{ _id: string; name: string; bus_sign: string; color: string; zone_id: string | null }>;
  incoming: Array<{
    _id: string;
    bus_name: string;
    distance_from_station_km: string;
    distance_from_station_min: number;
    line_sign: string;
    line_color: string;
    direction_towards: string;
  }>;
}

interface BusInfo {
  bus: {
    _id: string;
    name?: string;
    arvento_id?: string;
    number?: string;
    color: string;
    speed?: number;
    avg_15min?: number;
    is_active: boolean;
    is_visible: boolean;
    address?: string;
    zone?: ZoneRef | null;
    line?: { _id: string; name: string } | null;
    line_check?: LineCheck | null;
    heading?: number;
    geometry?: { type: 'Point'; coordinates: [number, number] };
    direction_v2?: {
      dir: 'going' | 'return' | null;
      confidence?: number;
      off_route?: boolean;
      stalled?: boolean;
      at_terminus?: boolean;
    };
  };
  towards: string | null;
  upcoming: Array<{
    _id: string;
    name: string;
    distance_to_station_km: string;
    distance_to_station_min: number | '> 30';
  }>;
  passed: number;
  message: string | null;
}

const icons = new Map<string, string>();
const iconOf = (key: string, draw: () => ImageData) => {
  if (!icons.has(key)) icons.set(key, iconUrl(draw()));
  return icons.get(key)!;
};

/** What the bus card shows (shared, so the map can draw the bus's line). */
export function useBusInfo(id: string | null | undefined) {
  return useQuery({
    queryKey: ['buses', 'info', id],
    queryFn: () => api<BusInfo>(`/buses/${id}/info`),
    refetchInterval: 10_000,
    enabled: !!id,
  });
}

/** Floating card on a map (top left), closed with ✕. */
function Panel({
  icon,
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="absolute top-2 left-2 z-20 flex max-h-[calc(100%-1rem)] w-80 flex-col overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-slate-200">
      <div className="flex items-start gap-3 border-b border-slate-200 px-3 py-2.5">
        <div className="shrink-0">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-slate-900">{title}</div>
          {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('Close')}
          className="text-slate-400 hover:text-slate-700"
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2.5 text-sm">{children}</div>
      {footer && <div className="border-t border-slate-200 px-3 py-2">{footer}</div>}
    </div>
  );
}

function LineSign({ sign, color }: { sign: string; color: string }) {
  const c = safeHex(color);
  return (
    <span
      className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold"
      style={{ background: c, color: textOn(c) }}
    >
      {sign}
    </span>
  );
}

/** Small text link to an edit page. */
function EditLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-xs font-medium whitespace-nowrap text-brand-700 hover:underline">
      {children}
    </Link>
  );
}

const minutesText = (m: number | '> 30') => (m === '> 30' ? '> 30 min' : t('{n} min', { n: m }));

/** Station info as the app shows it (lines, buses arriving within 30 minutes). */
export function useStationInfo(id: string | undefined) {
  return useQuery({
    queryKey: ['stations', 'info', id],
    queryFn: () => api<StationInfo>(`/stations/${id}/info`),
    refetchInterval: 15_000,
    enabled: !!id,
  });
}

/** Buses arriving at a station within 30 minutes, with minutes, as in the app. */
export function StationArrivals({
  incoming,
  onSelectBus,
}: {
  incoming: StationInfo['incoming'];
  onSelectBus?: (id: string) => void;
}) {
  return (
    <>
      {incoming.length === 0 ? (
        <p className="text-slate-500">{t('No bus on the way right now.')}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {incoming.map((b) => (
            <li key={`${b._id}-${b.line_sign}`}>
              <button
                type="button"
                disabled={!onSelectBus}
                onClick={() => onSelectBus?.(b._id)}
                title={onSelectBus ? t('Show this bus') : undefined}
                className="flex w-full items-center gap-2 py-1.5 text-left enabled:hover:bg-slate-50"
              >
                <LineSign sign={b.line_sign} color={b.line_color} />
                <div className="min-w-0 flex-1">
                  <div className="truncate">
                    <ArrowRight className="mr-1 inline size-3 text-slate-400" />
                    {b.direction_towards}
                  </div>
                  <div className="truncate text-xs text-slate-500">
                    {b.bus_name} · {b.distance_from_station_km} km
                  </div>
                </div>
                <span className="font-semibold text-slate-900">
                  {minutesText(b.distance_from_station_min)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** Station card: lines and buses arriving within 30 minutes, as the app shows them. */
export function StationPanel({
  id,
  onClose,
  onSelectBus,
}: {
  id: string;
  onClose: () => void;
  /** Opens a bus's card (from the arriving list). */
  onSelectBus?: (id: string) => void;
}) {
  const navigate = useNavigate();
  const info = useStationInfo(id);
  const d = info.data;
  return (
    <Panel
      icon={<img src={iconOf('station', () => drawStation())} alt="" className="size-7" />}
      title={d?.station.name ?? '…'}
      subtitle={t('Station')}
      onClose={onClose}
      footer={
        <Button
          size="sm"
          variant="secondary"
          icon={<Pencil className="size-4" />}
          onClick={() => navigate(`/stations/${id}`)}
        >
          {t('Edit station')}
        </Button>
      }
    >
      {!d ? (
        <Spinner />
      ) : (
        <div className="space-y-3">
          <ul className="space-y-1">
            {d.lines.map((l) => (
              <li key={l._id} className="flex items-center gap-1.5 text-xs text-slate-600">
                <LineSign sign={l.bus_sign} color={l.color} />
                <span className="min-w-0 flex-1 truncate">{l.name}</span>
                <EditLink to={`/directions/${l._id}`}>{t('Direction')}</EditLink>
                {l.zone_id && (
                  <>
                    <span className="text-slate-300">·</span>
                    <EditLink to={`/lines/${l.zone_id}`}>{t('Line')}</EditLink>
                  </>
                )}
              </li>
            ))}
          </ul>
          <div>
            <div className="mb-1 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              {t('Arriving within 30 minutes')}
            </div>
            <StationArrivals incoming={d.incoming} onSelectBus={onSelectBus} />
          </div>
        </div>
      )}
    </Panel>
  );
}

/** Bus card: state, direction and the next stations with minutes, as the app shows them. */
export function BusPanel({
  id,
  onClose,
  onSelectStation,
}: {
  id: string;
  onClose: () => void;
  /** Opens a station's card (from the next stations list). */
  onSelectStation?: (id: string) => void;
}) {
  const navigate = useNavigate();
  const info = useBusInfo(id);
  const d = info.data;
  const b = d?.bus;
  const color = b?.is_active ? safeHex(b.color) : '#94a3b8';
  const v2 = b?.direction_v2;
  return (
    <Panel
      icon={<img src={iconOf(`bus:${color}`, () => drawBus(color))} alt="" className="h-9 w-[18px]" />}
      title={b ? `${b.number ? `${b.number} · ` : ''}${b.name || b.arvento_id || ''}` : '…'}
      subtitle={b?.zone ? b.zone.name : t('No line')}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap gap-1.5">
          <Button
            size="sm"
            variant="secondary"
            icon={<Pencil className="size-4" />}
            onClick={() => navigate(`/buses/${id}`)}
          >
            {t('Edit bus')}
          </Button>
          {b?.line && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Route className="size-4" />}
              onClick={() => navigate(`/directions/${b.line!._id}`)}
            >
              {t('Direction')}
            </Button>
          )}
          {b?.zone && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Waypoints className="size-4" />}
              onClick={() => navigate(`/lines/${b.zone!._id}`)}
            >
              {t('Line')}
            </Button>
          )}
        </div>
      }
    >
      {!d || !b ? (
        <Spinner />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {b.is_active ? (
              <Badge tone="green">{t('In service')}</Badge>
            ) : (
              <Badge tone="blue">{t('Not in service')}</Badge>
            )}
            {!b.is_visible && <Badge tone="gray">{t('Hidden in the app')}</Badge>}
            {v2?.at_terminus && <Badge tone="blue">{t('At terminus')}</Badge>}
            {v2?.off_route && <Badge tone="red">{t('Off route')}</Badge>}
            {v2?.stalled && <Badge tone="amber">{t('Standing still')}</Badge>}
          </div>
          <LineCheckNotice busId={id} check={b.line_check} zone={b.zone ?? null} compact />
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-slate-500">{t('Direction')}</dt>
            <dd className="truncate">
              {b.line ? (
                <Link to={`/directions/${b.line._id}`} className="hover:underline">
                  {b.line.name}
                </Link>
              ) : (
                '—'
              )}
            </dd>
            <dt className="text-slate-500">{t('Towards')}</dt>
            <dd>{d.towards ?? '—'}</dd>
            <dt className="text-slate-500">{t('Direction (new logic)')}</dt>
            <dd>
              {v2?.dir ? (v2.dir === 'going' ? t('going') : t('return')) : '—'}
              {v2?.confidence !== undefined && v2.dir && (
                <span className="text-slate-400"> · {Math.round(v2.confidence * 100)}%</span>
              )}
            </dd>
            <dt className="text-slate-500">{t('Speed')}</dt>
            <dd>
              {b.speed ?? 0} km/h
              {b.avg_15min ? (
                <span className="text-slate-400"> · ⌀ {b.avg_15min.toFixed(1)} km/h</span>
              ) : null}
            </dd>
            {b.address && (
              <>
                <dt className="text-slate-500">{t('Address')}</dt>
                <dd className="truncate">{b.address}</dd>
              </>
            )}
          </dl>
          <div>
            <div className="mb-1 text-xs font-semibold tracking-wide text-slate-500 uppercase">
              {t('Next stations')}
            </div>
            {d.upcoming.length === 0 ? (
              <p className="text-slate-500">{d.message ? t(d.message) : t('No stations ahead.')}</p>
            ) : (
              <ol className="divide-y divide-slate-100">
                {d.upcoming.map((s) => (
                  <li key={s._id}>
                    <button
                      type="button"
                      disabled={!onSelectStation}
                      onClick={() => onSelectStation?.(s._id)}
                      title={onSelectStation ? t('Show this station') : undefined}
                      className="flex w-full items-center gap-2 py-1.5 text-left enabled:hover:bg-slate-50"
                    >
                      <span className="min-w-0 flex-1 truncate">{s.name}</span>
                      <span className="text-xs text-slate-500">{s.distance_to_station_km} km</span>
                      <span className="w-16 text-right font-semibold text-slate-900">
                        {minutesText(s.distance_to_station_min)}
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            )}
            {d.passed > 0 && (
              <p className="mt-1 text-xs text-slate-400">
                {t('{n} stations already passed', { n: d.passed })}
              </p>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}
